import { createContext, useContext, useCallback, useEffect } from "react";
import IEnvironment from "../../interfaces/IEnvironment";
import EnvironmentService from "../../services/EnvironmentService";
import NotFound from "../../components/NotFound";
import DocumentHelper from "../../helpers/DocumentHelper";
import Loading from "../../components/Loading";
import FormHelper from "../../helpers/FormHelper";
import { GovernanceStatusData, GovernanceStatusEnum, OccupancyStatusData, OccupancyStatusEnum } from "../../data/Enums";
import DefaultConstants from "../../data/Constants";
import useState from "../../hooks/useState";


interface IEnvironmentContext {
    environment: IEnvironment;
    handleChangeEnvironment: (data: IEnvironment) => void;
}

interface IStateValues {
    loadingEnvironment: boolean;
    environment: IEnvironment | undefined;
}


const EnvironmentContext = createContext<IEnvironmentContext | undefined>(undefined);

const EnvironmentContextProvider = (props: React.PropsWithChildren) => {
    const state = useState<IStateValues>({
        loadingEnvironment: true, 
        environment: undefined
    });


    useEffect(() => {
        handleRefreshEnvironment();
        // eslint-disable-next-line
    }, []);


    const handleRefreshEnvironment = useCallback(() => {
        const formData = FormHelper.Create(undefined);
        
        EnvironmentService.Get(formData).then(resp => {
            const environment = resp.data.environment as IEnvironment;
            
            if (environment.partnerPms.id === undefined) {
                throw new Error(`Unable to load selected partner PMS: "${environment.partnerPms.description}"`);
            }

            environment.partnerPms.systemName = environment.partnerPms.systemName ?? DefaultConstants.systemName;
            state.update('environment', environment);


            // Configuring status with partner IDs
            OccupancyStatusData[OccupancyStatusEnum.Available].partnerId = environment.partnerPms.occupancyAvailableId;
            OccupancyStatusData[OccupancyStatusEnum.Occupied].partnerId = environment.partnerPms.occupancyOccupiedId;

            GovernanceStatusData[GovernanceStatusEnum.Clean].partnerId = environment.partnerPms.governanceCleanId;
            GovernanceStatusData[GovernanceStatusEnum.Dirty].partnerId = environment.partnerPms.governanceDirtyId;
            GovernanceStatusData[GovernanceStatusEnum.Inspection].partnerId = environment.partnerPms.governanceInspectionId;
            GovernanceStatusData[GovernanceStatusEnum.InTidying].partnerId = environment.partnerPms.governanceInTidyingId;
            GovernanceStatusData[GovernanceStatusEnum.Maintenance].partnerId = environment.partnerPms.governanceMaintenanceId;
            GovernanceStatusData[GovernanceStatusEnum.Interdicted].partnerId = environment.partnerPms.governanceInterdictedId;


            DocumentHelper.setTitle(environment.partnerPms.systemName);
            DocumentHelper.setFavIcon(environment.images.icon);
        }).finally(() => {
            state.update('loadingEnvironment', false);
        });
        // eslint-disable-next-line
    }, []);


    const handleChangeEnvironment = useCallback((data: IEnvironment) => {
        state.update('environment', {
            ...state.values.environment,
            ...data
        });

        // eslint-disable-next-line
    }, [state.values.environment]);


    if (state.values.loadingEnvironment) {
        return (
            <Loading open={true} className='loading-environment' />
        );
    } else if (state.values.environment === undefined) {
        return (
            <NotFound description="_environmentNotFoundDescription" />
        );
    } else {
        const data: IEnvironmentContext = {
            environment: state.values.environment,
            handleChangeEnvironment
        };
    
        return (
            <EnvironmentContext.Provider value={data}>
                {props.children}
            </EnvironmentContext.Provider>
        );
    }
}


const useEnvironmentContext = () => {
    const context = useContext(EnvironmentContext);

    if (context === undefined) {
        throw new Error('useEnvironmentContext must be used within a EnvironmentContextProvider');
    }

    return context;
}

export {
    useEnvironmentContext
}

export default EnvironmentContextProvider;